import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEffect } from "react";

export interface Booking {
  id: string;
  booking_id: string;
  property_id: string;
  property_short_name: string;
  property_name?: string;
  guest_id: string | null;
  guest_name: string;
  guest_email: string | null;
  guest_phone: string | null;
  origin_id: string;
  check_in: string;
  check_out: string;
  nights: number;
  total_guests: number;
  children: number | null;
  total_payment: number;
  cleaning_fee: number | null;
  base_payment: number | null;
  pets_fee: number | null;
  early_checkin_fee: number | null;
  late_checkout_fee: number | null;
  other_fees: number | null;
  estimated_payout: number | null;
  stay_completed: boolean | null;
  paid: boolean | null;
  status: "Reservada" | "Cancelada";
  note: string | null;
  created_at: string;
  updated_at: string;
  origins?: {
    name: string;
  };
}

export interface BookingFormData {
  property_id: string;
  origin_id: string;
  check_in: string;
  check_out: string;
  guest_name: string;
  guest_email?: string;
  guest_phone?: string;
  total_guests: number;
  children?: number;
  total_payment: number;
  cleaning_fee?: number;
  base_payment?: number;
  pets_fee?: number;
  early_checkin_fee?: number;
  late_checkout_fee?: number;
  other_fees?: number;
  estimated_payout?: number;
  stay_completed?: boolean;
  note?: string;
  guest_id?: string;
}

// Hook para manejar subscripciones en tiempo real para reservaciones
const useBookingsRealtime = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Crear un identificador único para evitar conflictos de canales
    const channelId = `bookings-changes-${Math.random().toString(36).substr(2, 9)}`;
    
    const channel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["bookings"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
};

export const useBookings = () => {
  useBookingsRealtime();
  
  return useQuery({
    queryKey: ["bookings"],
    queryFn: async (): Promise<Booking[]> => {
      const { data: bookings, error } = await supabase
        .from("bookings")
        .select(
          `
          *,
          origins (
            name
          )
        `,
        )
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return (bookings || []).map((booking) => ({
        ...booking,
        status: booking.status as "Reservada" | "Cancelada",
      }));
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateBooking = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (bookingData: BookingFormData): Promise<Booking> => {
      let guestId = bookingData.guest_id;

      if (!guestId) {
        const { data: existingGuests } = await supabase
          .from("guests")
          .select("id")
          .eq("name", bookingData.guest_name)
          .limit(1);

        if (existingGuests && existingGuests.length > 0) {
          guestId = existingGuests[0].id;
        } else {
        const { data: newGuest, error: guestError } = await supabase
          .from("guests")
          .insert({
            name: bookingData.guest_name,
            email: bookingData.guest_email || null,
            phone: bookingData.guest_phone || null,
            origin_id: bookingData.origin_id,
          })
          .select("id")
          .single();

          if (guestError) {
            console.error("Error creating guest:", guestError);
            throw guestError;
          }

          guestId = newGuest.id;
        }
      }

      const checkIn = new Date(bookingData.check_in);
      const checkOut = new Date(bookingData.check_out);
      const nights = Math.ceil(
        (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24),
      );

      const { data: booking, error } = await supabase
        .from("bookings")
        .insert({
          property_id: bookingData.property_id,
          origin_id: bookingData.origin_id,
          check_in: bookingData.check_in,
          check_out: bookingData.check_out,
          guest_id: guestId,
          guest_name: bookingData.guest_name,
          guest_email: bookingData.guest_email || null,
          guest_phone: bookingData.guest_phone || null,
          total_guests: bookingData.total_guests,
          children: bookingData.children || 0,
          total_payment: bookingData.total_payment,
          cleaning_fee: bookingData.cleaning_fee || 0,
          base_payment: bookingData.base_payment || 0,
          pets_fee: bookingData.pets_fee || 0,
          early_checkin_fee: bookingData.early_checkin_fee || 0,
          late_checkout_fee: bookingData.late_checkout_fee || 0,
          other_fees: bookingData.other_fees || 0,
          estimated_payout: bookingData.estimated_payout || 0,
          stay_completed: bookingData.stay_completed || false,
          note: bookingData.note || null,
          nights: nights,
          property_short_name: "",
          booking_id: "",
        })
        .select(
          `
          id,
          booking_id,
          property_id,
          property_short_name,
          guest_id,
          guest_name,
          guest_email,
          guest_phone,
          origin_id,
          check_in,
          check_out,
          nights,
          total_guests,
          children,
          total_payment,
          cleaning_fee,
          base_payment,
          pets_fee,
          early_checkin_fee,
          late_checkout_fee,
          other_fees,
          estimated_payout,
          stay_completed,
          paid,
          status,
          note,
          created_at,
          updated_at,
          origins!inner (
            name
          )
        `,
        )
        .single();

      if (error) {
        console.error("Error creating booking:", error);
        throw error;
      }

      return {
        ...booking,
        status: booking.status as "Reservada" | "Cancelada",
      } as Booking;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["availability"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-data"] });
      toast({
        title: "Reservación creada",
        description: "La reservación ha sido creada exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Error al crear la reservación: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateBooking = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      bookingData,
    }: {
      id: string;
      bookingData: BookingFormData;
    }): Promise<Booking> => {
      let guestId = bookingData.guest_id;

      if (!guestId) {
        const { data: existingGuests } = await supabase
          .from("guests")
          .select("id")
          .eq("name", bookingData.guest_name)
          .limit(1);

        if (existingGuests && existingGuests.length > 0) {
          guestId = existingGuests[0].id;
        } else {
          const { data: newGuest, error: guestError } = await supabase
            .from("guests")
            .insert({
              name: bookingData.guest_name,
              email: bookingData.guest_email || null,
              phone: bookingData.guest_phone || null,
              origin_id: bookingData.origin_id,
            })
            .select("id")
            .single();

          if (guestError) {
            console.error("Error creating guest:", guestError);
            throw guestError;
          }

          guestId = newGuest.id;
        }
      }

      const { data: booking, error } = await supabase
        .from("bookings")
        .update({
          property_id: bookingData.property_id,
          origin_id: bookingData.origin_id,
          check_in: bookingData.check_in,
          check_out: bookingData.check_out,
          guest_id: guestId,
          guest_name: bookingData.guest_name,
          guest_email: bookingData.guest_email || null,
          guest_phone: bookingData.guest_phone || null,
          total_guests: bookingData.total_guests,
          children: bookingData.children || 0,
          total_payment: bookingData.total_payment,
          cleaning_fee: bookingData.cleaning_fee || 0,
          base_payment: bookingData.base_payment || 0,
          pets_fee: bookingData.pets_fee || 0,
          early_checkin_fee: bookingData.early_checkin_fee || 0,
          late_checkout_fee: bookingData.late_checkout_fee || 0,
          other_fees: bookingData.other_fees || 0,
          estimated_payout: bookingData.estimated_payout || 0,
          stay_completed: bookingData.stay_completed || false,
          note: bookingData.note || null,
        })
        .eq("id", id)
        .select(
          `
          id,
          booking_id,
          property_id,
          property_short_name,
          guest_id,
          guest_name,
          guest_email,
          guest_phone,
          origin_id,
          check_in,
          check_out,
          nights,
          total_guests,
          children,
          total_payment,
          cleaning_fee,
          base_payment,
          pets_fee,
          early_checkin_fee,
          late_checkout_fee,
          other_fees,
          estimated_payout,
          stay_completed,
          paid,
          status,
          note,
          created_at,
          updated_at,
          origins!inner (
            name
          )
        `,
        )
        .single();

      if (error) {
        console.error("Error updating booking:", error);
        throw error;
      }

      return {
        ...booking,
        status: booking.status as "Reservada" | "Cancelada",
      } as Booking;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["availability"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-data"] });
      toast({
        title: "Reservación actualizada",
        description: "La reservación ha sido actualizada exitosamente",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Error al actualizar la reservación: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateBookingStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: "Reservada" | "Cancelada";
    }) => {
      const { data: booking, error } = await supabase
        .from("bookings")
        .update({ status })
        .eq("id", id)
        .select(
          `
          *,
          origins (
            name
          )
        `,
        )
        .single();

      if (error) {
        console.error("Error updating booking status:", error);
        throw error;
      }

      return {
        ...booking,
        status: booking.status as "Reservada" | "Cancelada",
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["availability"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-data"] });
      toast({
        title: "Estado actualizado",
        description: `La reservación ha sido ${variables.status.toLowerCase()}`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Error al actualizar el estado: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useSearchGuests = (searchTerm: string) => {
  return useQuery({
    queryKey: ["guests-search", searchTerm],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 2) return [];

      const { data: guests, error } = await supabase
        .from("guests")
        .select("*")
        .or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
        .limit(10);

      if (error) {
        console.error("Error searching guests:", error);
        throw error;
      }

      return guests || [];
    },
    enabled: searchTerm.length >= 2,
    staleTime: 30 * 1000,
  });
};
